'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface QuizQuestion {
  id: number;
  question: string;
  options: string[];
}

interface QuizResult {
  style: string;
  description: string;
  recommendations: string[];
}

const DesignQuiz = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<string[]>([]);
  const [showResult, setShowResult] = useState(false);
  const [quizStarted, setQuizStarted] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const questions: QuizQuestion[] = [
    {
      id: 1,
      question: 'What best describes your ideal living space?',
      options: [
        'Clean lines and minimal clutter',
        'Warm textures and traditional elements',
        'Bold colors and eclectic mix',
        'Natural materials and earthy tones'
      ]
    },
    {
      id: 2,
      question: 'Which color palette appeals to you most?',
      options: [
        'Whites, grays, and blacks',
        'Warm terracotta, gold, and deep greens',
        'Vibrant jewel tones',
        'Neutral beiges and natural wood tones'
      ]
    },
    {
      id: 3,
      question: 'How do you want your home to feel?',
      options: [
        'Calm and serene',
        'Culturally rich and welcoming',
        'Energetic and inspiring',
        'Grounded and peaceful'
      ]
    },
    {
      id: 4,
      question: 'What type of furniture do you prefer?',
      options: [
        'Sleek and contemporary',
        'Handcrafted with traditional details',
        'Mix of vintage and modern',
        'Sustainable and organic'
      ]
    },
    {
      id: 5,
      question: 'How important is cultural heritage in your design?',
      options: [
        'Not very important',
        'Very important - I want it prominently featured',
        'Somewhat important - subtle touches',
        'Important through sustainable local materials'
      ]
    }
  ];

  const getQuizResult = (): QuizResult => {
    const styleScores: { [key: string]: number } = {
      modern: 0,
      traditional: 0,
      eclectic: 0,
      organic: 0
    };

    answers.forEach((answer, index) => {
      const optionIndex = questions[index].options.indexOf(answer);
      if (optionIndex === 0) styleScores.modern++;
      if (optionIndex === 1) styleScores.traditional++;
      if (optionIndex === 2) styleScores.eclectic++;
      if (optionIndex === 3) styleScores.organic++;
    });

    const topStyle = Object.entries(styleScores).sort((a, b) => b[1] - a[1])[0][0];

    const results: { [key: string]: QuizResult } = {
      modern: {
        style: 'Contemporary Minimalist',
        description: 'You appreciate clean lines, functional spaces, and a clutter-free environment. Your ideal home balances simplicity with sophistication.',
        recommendations: [
          'Modular furniture with hidden storage',
          'Neutral color palette with accent pieces',
          'Smart home integration',
          'Open-plan living spaces'
        ]
      },
      traditional: {
        style: 'Cultural Heritage',
        description: 'You value traditional Indian craftsmanship and want your home to reflect cultural richness while maintaining modern comfort.',
        recommendations: [
          'Handcrafted furniture with traditional motifs',
          'Rich textiles and artisan pieces',
          'Culturally-aware layouts',
          'Heritage color palettes'
        ]
      },
      eclectic: {
        style: 'Modern Eclectic',
        description: 'You love mixing styles and creating unique spaces that tell your personal story through diverse design elements.',
        recommendations: [
          'Mix of vintage and contemporary pieces',
          'Bold accent walls and artwork',
          'Statement lighting fixtures',
          'Curated collections display'
        ]
      },
      organic: {
        style: 'Natural Organic',
        description: 'You prioritize sustainability and natural materials, creating spaces that feel grounded and connected to nature.',
        recommendations: [
          'Sustainable local materials',
          'Biophilic design elements',
          'Natural light optimization',
          'Indoor plants and green walls'
        ]
      }
    };

    return results[topStyle];
  };

  const handleAnswer = (answer: string) => {
    const newAnswers = [...answers, answer];
    setAnswers(newAnswers);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResult(true);
    }
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setAnswers([]);
    setShowResult(false);
    setQuizStarted(false);
  };

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-2xl shadow-soft p-8 lg:p-12">
        <div className="h-8 bg-muted/20 rounded w-2/3 mb-4 animate-pulse" />
        <div className="h-4 bg-muted/20 rounded w-full mb-8 animate-pulse" />
        <div className="space-y-4">
          <div className="h-12 bg-muted/20 rounded animate-pulse" />
          <div className="h-12 bg-muted/20 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  if (!quizStarted) {
    return (
      <div className="bg-gradient-to-br from-accent/10 to-primary/10 rounded-2xl shadow-soft p-8 lg:p-12">
        <div className="text-center max-w-2xl mx-auto">
          <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center mx-auto mb-6">
            <Icon name="SparklesIcon" size={40} className="text-primary-foreground" />
          </div>
          <h2 className="font-headline text-3xl font-bold text-foreground mb-4">
            Discover Your Design Style
          </h2>
          <p className="font-body text-muted-foreground mb-8">
            Take our quick 5-question quiz to find out which interior design style matches your personality and preferences. Get personalized recommendations for your dream home!
          </p>
          <button
            onClick={() => setQuizStarted(true)}
            className="bg-primary text-primary-foreground font-body font-semibold px-8 py-4 rounded-lg hover:bg-primary/90 transition-all duration-200 shadow-soft hover:shadow-strong inline-flex items-center space-x-2"
          >
            <span>Start Quiz</span>
            <Icon name="ArrowRightIcon" size={20} />
          </button>
        </div>
      </div>
    );
  }

  if (showResult) {
    const result = getQuizResult();
    
    return (
      <div className="bg-card rounded-2xl shadow-soft p-8 lg:p-12">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Icon name="CheckCircleIcon" size={40} className="text-success" variant="solid" />
          </div>
          <h2 className="font-headline text-3xl font-bold text-foreground mb-2">
            Your Design Style
          </h2>
          <p className="font-headline text-2xl text-primary mb-4">{result.style}</p>
          <p className="font-body text-muted-foreground max-w-2xl mx-auto">
            {result.description}
          </p>
        </div>

        <div className="bg-background rounded-xl p-6 mb-8">
          <h3 className="font-body font-semibold text-foreground mb-4 flex items-center">
            <Icon name="LightBulbIcon" size={20} className="text-accent mr-2" />
            Recommended Design Elements
          </h3>
          <ul className="space-y-3">
            {result.recommendations.map((rec, index) => (
              <li key={index} className="flex items-start">
                <Icon name="CheckIcon" size={16} className="text-success mt-1 mr-3 flex-shrink-0" />
                <span className="font-body text-sm text-muted-foreground">{rec}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={resetQuiz}
            className="px-6 py-3 border border-border text-foreground font-body font-medium rounded-lg hover:bg-background transition-all duration-200"
          >
            Retake Quiz
          </button>
          <button
            onClick={() => {
              const formSection = document.getElementById('contact-form');
              if (formSection) {
                formSection.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="px-6 py-3 bg-primary text-primary-foreground font-body font-semibold rounded-lg hover:bg-primary/90 transition-all duration-200 shadow-soft"
          >
            Schedule Consultation
          </button>
        </div>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div className="bg-card rounded-2xl shadow-soft p-8 lg:p-12">
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <span className="font-body text-sm text-muted-foreground">
            Question {currentQuestion + 1} of {questions.length}
          </span>
          <span className="font-body text-sm font-medium text-primary">
            {Math.round(progress)}% Complete
          </span>
        </div>
        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <h3 className="font-headline text-2xl font-bold text-foreground mb-8">
        {questions[currentQuestion].question}
      </h3>

      <div className="space-y-4">
        {questions[currentQuestion].options.map((option, index) => (
          <button
            key={index}
            onClick={() => handleAnswer(option)}
            className="w-full text-left p-6 border-2 border-border rounded-xl hover:border-primary hover:bg-primary/5 transition-all duration-200 group"
          >
            <div className="flex items-center justify-between">
              <span className="font-body text-foreground group-hover:text-primary transition-colors">
                {option}
              </span>
              <Icon
                name="ArrowRightIcon"
                size={20}
                className="text-muted-foreground group-hover:text-primary transition-colors"
              />
            </div>
          </button>
        ))}
      </div>

      {currentQuestion > 0 && (
        <button
          onClick={() => {
            setCurrentQuestion(currentQuestion - 1);
            setAnswers(answers.slice(0, -1));
          }}
          className="mt-6 text-muted-foreground hover:text-foreground font-body text-sm flex items-center transition-colors"
        >
          <Icon name="ArrowLeftIcon" size={16} className="mr-2" />
          Previous Question
        </button>
      )}
    </div>
  );
};

export default DesignQuiz;